import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { FeedbackResourceService } from '../../../../api-gen/gen-server/services/feedback-resource.service';
import { MessageService } from '../../service/message.service';

export interface UpdateInfo {
  date: string;
  updates: string[];
  image?: string;
  video?: string;
}

@Component({
  selector: 'app-updates',
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDividerModule,
  ],
  templateUrl: './updates.component.html',
  styleUrl: './updates.component.scss',
})
export class UpdatesComponent {
  private feedbackService = inject(FeedbackResourceService);
  private messageService = inject(MessageService);

  updates: UpdateInfo[] = [
    {
      date: '2024-11-11',
      updates: ['Added Realism AI Model (Beta Version)'],
    },
    {
      date: '2024-12-02',
      updates: ['Added Cartoon and Anime AI Model'],
    },
    {
      date: '2025-01-15',
      updates: ['Added Option for Templates (LoRas)'],
    },
    {
      date: '2025-02-20',
      updates: ['Added Video Generation Model. Supporting Image-To-Video'],
    },
    {
      date: '2025-03-10',
      updates: ['Added Image Edit AI Model for image editing (Inpaint)'],
    },
    {
      date: '2025-06-05',
      updates: ['Added updated Video Generation Model with better quality'],
    },
    {
      date: '2025-07-18',
      updates: ['Added community posts and weekly challenges'],
    },
    {
      date: '2025-08-18',
      updates: ['Added Limited AI Model: Dreamy Realism'],
    },
    {
      date: '2025-09-10',
      updates: ['Added NanoBanana AI Model for image editing'],
      image: 'assets/updates/update_nanobanana.webp',
    },
    {
      date: '2025-09-15',
      updates: ['Added Video Editing: Increase FPS option'],
    },
    {
      date: '2025-09-21',
      updates: ['Added Developer API'],
    },
    {
      date: '2025-09-22',
      updates: ['Improved Multi Face Scenario Handling'],
    },
    {
      date: '2025-10-25',
      updates: ['Updated Video Generation Model'],
    },
    {
      date: '2025-11-02',
      updates: ['Added new Image Editing AI Model'],
      image: 'assets/updates/update_edit.webp',
    },
    {
      date: '2025-11-11',
      updates: ['Added Image Upscale', 'Added Pose Change to Image Edit'],
      image: 'assets/updates/update_pose.webp',
    },
    {
      date: '2025-11-20',
      updates: ['Added Custom Clothes to Image Edit'],
      image: 'assets/updates/update_clothes.webp',
    },
    {
      date: '2025-12-12',
      updates: ['Added Consistent AI Profiles'],
      image: 'assets/updates/update_char.webp',
    },
    {
      date: '2025-12-15',
      updates: ['Added 4k Image Upscale'],
      video: 'assets/updates/demo_4k.mp4',
    },
    {
      date: '2025-12-21',
      updates: ['Added Nano Banana Pro for Image Editing'],
      image: 'assets/updates/update_nanobanana_pro.webp',
    },
    {
      date: '2025-12-25',
      updates: [
        'Updated Image Editing Model. Edit and keep character consistency.',
      ],
      image: 'assets/updates/demoedit.webp',
    },
    {
      date: '2026-01-02',
      updates: [
        'Added Face Improvement edit option to enhance facial details in images',
        'Added Face Creation AI Model to generate realistic faces',
        'Added Nano Banana as a Face Match option',
      ],
      image: 'assets/updates/demo_face.webp',
    },
    {
      date: '2026-01-14',
      updates: [
        'Added Image Edit with camera angle change to modify perspective',
      ],
      image: 'assets/updates/demo_camera_angle.webp',
    },
    {
      date: '2026-01-23',
      updates: [
        'Added Enhance Image option to improve overall image quality and details',
      ],
      image: 'assets/updates/update_enhance.webp',
    },
    {
      date: '2026-02-07',
      updates: ['Added Realism Edit Model'],
      image: 'assets/updates/update_edit_realism.webp',
    },
    {
      date: '2026-02-13',
      updates: ['Train your own AI Character'],
      image: 'assets/updates/update_train.webp',
    },
    {
      date: '2026-02-17',
      updates: ['Create Video with Audio'],
    },
    {
      date: '2026-02-27',
      updates: ['New Character Creation AI Model'],
      image: 'assets/updates/charcreator.webp',
    },
    {
      date: '2026-03-03',
      updates: [
        'New Video Generation Model with better quality and faster speed',
      ],
      video: 'assets/updates/demo_video_audio.mp4',
    },
    {
      date: '2026-03-03',
      updates: [
        'Updated Video with Audio AI Model for better video quality and lip sync',
      ],
      video: 'assets/updates/update_video_audio.mp4',
    },
    {
      date: '2026-04-20',
      updates: [
        'Added new Edit Model "Edit Pro" for the best prompt understanding and character consistency ',
        'Updated the Creative Edit Model to "Creative 2.0"',
      ],
      image: 'assets/updates/update_edit_pro.webp',
    },
    {
      date: '2026-04-23',
      updates: ['Added new Anime Model Anima 0.3'],
      image: 'assets/updates/update_anima.webp',
    },
    {
      date: '2026-04-28',
      updates: [
        'Added new Video Model "Video Pro" for the best video quality and speech',
      ],
      video: 'assets/updates/update_video_pro_web.mp4',
    },
    {
      date: '2026-05-02',
      updates: ['Added Chat with Character with consistent Images'],
      image: 'assets/updates/update_chat.webp',
    },
    {
      date: '2026-05-11',
      updates: ['Added Grok AI Model for Image Creation And Image Edit'],
      image: 'assets/updates/update_grok_image.webp',
    },
    {
      date: '2026-06-11',
      updates: ['Added Social Media AI Model for Image Creation'],
      image: 'assets/updates/update_socialmedia.webp',
    },
    {
      date: '2026-06-24',
      updates: ['Updated Video with Audio'],
      video: 'assets/updates/update_video_audio_preview.mp4',
    },
    {
      date: '2026-06-29',
      updates: ['Added Krea Uncensored Model for Image Creation'],
      image: 'assets/updates/update_krea.webp',
    },
    {
      date: '2026-07-12',
      updates: [
        'Added Krea Edit Model for Image Editing with perfect character consistency',
      ],
      image: 'assets/updates/update_krea_edit.webp',
    },
    {
      date: '2026-07-20',
      updates: ['Added Outfit reference to Krea'],
      image: 'assets/updates/update_krea_outfit.webp',
    },
  ];
  feedback: string = '';
  isSubmitting = false;

  onSubmitClicked() {
    if (!this.feedback.trim()) {
      return;
    }

    this.isSubmitting = true;
    const request = {
      text: this.feedback,
    };

    this.feedbackService.feedbackPost({ body: request }).subscribe(() => {
      this.messageService.showMessage('Feedback submitted');
      this.feedback = '';
      this.isSubmitting = false;
    });
  }

  get reversedUpdates() {
    return [...this.updates].reverse();
  }
}
